/**************************************************************************************************/
/** @import Classes/Records                                                                      **/
/**************************************************************************************************/
import FieldsOptions from '@/Classes/Records/FieldsOptions'
import { ItemMissingFields } from '@/Classes/Records/ItemMissingFields'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { Component } from '@/Constants/Component'

/**************************************************************************************************/
/** @import Helpers/Components                                                                   **/
/**************************************************************************************************/
import { ActionField } from '@/Helpers/Components/DataTable/ActionField'
import { BMInterfaces } from '@/Helpers/Components/DataTable/BMInterfaces'
import { BMTypes } from '@/Helpers/Components/DataTable/BMTypes'

/**************************************************************************************************/
/** @export Class @name BuilderManager                                                           **/
/**************************************************************************************************/
export class BuilderManager
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	private actions = [] as Array<string>
	private fields  = [] as Array<BMInterfaces.FieldDefinition>
	private items   = [] as Array<BMInterfaces.ItemDefinition>

	/*************************************************/
	/** @Constructor                                **/
	/*************************************************/
	constructor(data: Array<any>, actions?: Array<ActionField>)
	{
		// Generar los campos que serán mostrados como cabezera en el componente 'DataTable'.
		if (data.length > 0)
		{
			for (const fieldName of Object.keys(data[0]))
			{
				this.fields.push({ key: fieldName, label: fieldName, thStyle: Component.VueComponents.Global.DataTable.DefaultThStyle })
			}
		}

		// Opcionalmente, pueden ser agregado campos de 'Acciones'.
		if (Array.isArray(actions))
		{
			for (const action of actions)
			{
				if (action?.field)
				{
					this.fields.push({ isAction: true, ...action.field })
					this.actions.push(action.field.key)
				}
			}
		}

		// Generar los items o filas que serán mostradas en el cuerpo del componente 'DataTable'.
		for (const dataRow of data)
		{
			const row = {} as BMInterfaces.ItemDefinition

			// Agregar las propiedades que conformarán un 'Item'.
			for (const entry of Object.entries(dataRow))
			{
				row[entry[0]] = entry[1]
				row._showDetails = false
			}

			// Opcionalmente, pueden ser agregado campos de 'acciones'.
			if (Array.isArray(actions))
			{
				for (const action of actions)
				{
					if (action?.items)
					{
						row[action.field.key] = []

						for (const item of action.items)
						{
							// Propiedad '$onItemCondition' permite retornar un objeto segun la resolución de la función con el parametro 'item'.
							if ('$onItemCondition' in item)
							{
								const resolved = item.$onItemCondition(row)
								this.pushActionsToItemArray(row, action, resolved)
							}

							// Validar las propiedades de los objetos directamente, verificando el tipo y agregar las opciones al objeto 'item' finalmente.
							else
							{
								this.pushActionsToItemArray(row, action, item)
							}
						}
					}
				}
			}

			this.items.push(row)
		}
	}

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	private addMissingOptionsFields(fields: Array<any>, options: any) : void
	{
		// Eliminar de 'options', los campos que ya se encuentran.
		for (const $f of fields)
		{
			if ($f.key in options)
			{
				delete options[$f.key]
			}
		}

		// Agregar a 'fields', los campos que quedaron en 'options'.
		for (const $o of Object.keys(options))
		{
			fields.push({ key: $o, ...options[$o] })
		}
	}

	public forEachCell(executor: (key: string, item: any) => void) : void
	{
		for (const item of this.items)
		{
			for (const key in item)
			{
				executor(key, item)
			}
		}
	}

	public forEachField(executor: (item: any) => void) : void
	{
		for (const f of this.fields) executor(f)
	}

	public forEachItem(executor: (item: any) => void) : void
	{
		for (const i of this.items) executor(i)
	}

	public generateDetailsFromItemsValueExcepts(...skipFields: Array<string>) : void
	{
		for (const item of this.items)
		{
			item._details = {}

			for (const itemKey in item)
			{
				if (itemKey === '_details' || itemKey === '_showDetails') continue
				const skipProp = skipFields.find((x) => x === itemKey)
				if (skipProp) continue
				item._details[itemKey] = item[itemKey]?.value
			}
		}
	}

	public pack() : { fields: Array<BMInterfaces.FieldDefinition>, items: Array<BMInterfaces.ItemDefinition> }
	{
		return { fields: this.fields, items: this.items }
	}

	public packWithActions() : { fields: Array<BMInterfaces.FieldDefinition>, items: Array<BMInterfaces.ItemDefinition>, actions: Array<string> }
	{
		return { fields: this.fields, items: this.items, actions: this.actions }
	}

	private parseActionsOptionsForButtonType(row: any, item: any) : any
	{
		// Propiedades para una Acción de tipo 'button'.
		const options: any =
		{
			key: item.key,
			text: item?.$textFrom ? row[item.$textFrom] : item.text,
			type: item.type,
			variant: item?.$colorBy ? item.$colorFrom[row[item.$colorBy]] : item?.variant ? item.variant : 'gray'
		}

		// Propiedades Opcionales.
		if ('enumFrom' in item) options.enum = item.enumFrom[row[item.enumBy]]
		if ('permission' in item) options.permission = item.permission
		if ('sortBy' in item) options.sort = item.sortBy

		return options
	}

	public parseFieldsOptions(options: FieldsOptions) : void
	{
		for (const field of this.fields)
		{
			// Verificar que existen opciones asociadas al campo.
			if (options.options?.[field.key])
			{
				// Las opciones especificas para el campo actual.
				const $options = options.options[field.key]

				// Iterar entras las opciones posibles.
				for (const $o of Object.entries($options))
				{
					const param: { k: string, v: any } = { k: $o[0], v: $o[1] }
					field[param.k] = param.v
				}
			}
		}

		// Agregar los campos que no se encuentran en 'fields' pero si en 'options'
		this.addMissingOptionsFields(this.fields, options.options)
	}

	public parseItemsDetail(formatters: BMTypes.ItemDetailsDictionary) : void
	{
		for (const item of this.items)
		{
			item._details = {}

			for (const key in formatters)
			{
				if (Array.isArray(formatters[key]))
				{
					item._details[key] = formatters[key]
				}
				else
				{
					const f = formatters[key] as Function
					item._details[key] = f(item)
				}
			}
		}
	}

	public parseItemsFormatters(formatters: { [key: string]: (itemValue: any, item?: BMInterfaces.ItemDefinition) => any }) : void
	{
		for (const item of this.items)
		{
			for (const obj of Object.entries(item))
			{
				// Verificar si existe alguna propiedad en formatters.
				const param = { key: obj[0], value: obj[1] }

				if (formatters?.[param.key])
				{
					item[param.key] = formatters[param.key](param.value, item)
				}
			}
		}
	}

	public parseItemsMissingFields(params: ItemMissingFields) : void
	{
		const customValues = { autoIncrement: 0 }

		for (const itemtem of this.items)
		{
			for (const key of Object.keys(params.getFields()))
			{
				if (!itemtem?.[key])
				{
					const value = params.getField(key).text

					if (value === 'AUTO_INCREMENT')
					{
						itemtem[key] = ++customValues.autoIncrement
					}
					else
					{
						itemtem[key] = value
					}
				}
			}
		}
	}

	private pushActionsToItemArray(row: any, actionParent: any, item: any) : void
	{
		// Propiedades con los tipos de 'elementos' predefinidos que pueden ser mostrados en el componente <DataTable>.
		if (item)
		{
			if (item.type === 'button')
			{
				const parsedOptions = this.parseActionsOptionsForButtonType(row, item)
				row[actionParent.field.key].push(parsedOptions)
			}
			else if (item.type === 'check')
			{
				row[actionParent.field.key].push({ checked: false, ...item })
			}
			else if (item.type === 'icon')
			{
				row[actionParent.field.key].push(item)
			}
		}
	}

	public rearrangeFields(...rearrange: Array<string>) : void
	{
		let index = 0

		for (const key of rearrange)
		{
			for (const field of this.fields)
			{
				if (key === field.key)
				{
					const i = this.fields.findIndex((x) => x.key === key)
					this.fields.splice(i, 1)
					this.fields.splice(index++, 0, field)
					break
				}
			}
		}
	}

	public removeFields(...names: Array<string>) : void
	{
		for (let i = 0; i < this.fields.length; i++)
		{
			const $f = this.fields[i]

			if (names.includes($f.key))
			{
				this.fields.splice(i, 1)
				i--
			}
		}
	}

	public removeItems(...options: Array<{ if?: boolean, field: string, value: any }>) : void
	{
		for (const $option of options)
		{
			const { field, value } = $option

			for (let i = 0; i < this.items.length; i++)
			{
				const item = this.items[i]

				if (field in item)
				{
					if ('if' in $option && $option.if === true)
					{
						if (item[field] === value)
						{
							this.items.splice(i, 1)
							i--
						}
					}
					else
					{
						if (item[field] === value)
						{
							this.items.splice(i, 1)
							i--
						}
					}
				}
			}
		}
	}

	public showFields(...names: Array<string>) : void
	{
		for (let i = 0; i < this.fields.length; i++)
		{
			const field = this.fields[i]

			if (!names.includes(field.key))
			{
				this.fields.splice(i, 1)
				i--
			}
		}
	}
}